import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import { useState, useEffect, useMemo } from 'react';
import { sandhillsRequest, toTitleCase } from '@Components/Utilities/SandhillsUtility';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { gtmPushEvent } from '@Components/Utilities/SandhillsUtility';
import { gtmEventType } from '@Components/Utilities/SandhillsConstants';

const TowingCapacityCalculator = (props) => {
    const routes = useRecoilValue(atoms.routes);
    const [validationErrors, setValidationErrors] = useState({});
    const [formData, setFormData] = useState(getInitialFormData());
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    function getInitialFormData(vin = null) {
        return {
            yearOptions: mapOptions(props.YearOptions),
            makeOptions: [],
            modelOptions: [],
            trimOptions: [],
            year: { Text: '', Value: '' },
            yearInput: '',
            make: { Text: '', Value: '' },
            makeInput: '',
            model: { Text: '', Value: '' },
            modelInput: '',
            trim: { Text: '', Value: '' },
            trimInput: '',
            vin: vin ?? '',
        };
    }
    const [yearOpen, setYearOpen] = useState(false);
    const [makeOpen, setMakeOpen] = useState(false);
    const [modelOpen, setModelOpen] = useState(false);
    const [trimOpen, setTrimOpen] = useState(false);

    const yearLoading = useMemo(() => yearOpen && formData?.yearOptions?.length === 0, [yearOpen, formData.yearOptions]);
    const makeLoading = useMemo(() => makeOpen && formData?.makeOptions?.length === 0, [makeOpen, formData.makeOptions]);
    const modelLoading = useMemo(() => modelOpen && formData?.modelOptions?.length === 0, [modelOpen, formData.modelOptions]);
    const trimLoading = useMemo(() => trimOpen && formData?.trimOptions?.length === 0, [trimOpen, formData.trimOptions]);

    useEffect(() => {
        // on component mount, if props.searchTruckCampers is changed and all form fields are filled, fetch towing capacity results
        if (formData.year?.Text != '' && formData.make?.Text != '' && formData.model?.Text != '' && formData.trim?.Text != '') {
            getVehicleTowingCapcityResults(formData.year?.Text, formData.make?.Text, formData.model?.Text, formData.trim?.Text, props.searchTruckCampers);
        }
        else if (formData.vin != '') {
            getOptionsByVin(formData.vin, props.searchTruckCampers);
        }
        else {
            props.setTowingResults(null, false, true);
        }
    }, [props.searchTruckCampers]); 

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true); 
        let rules = [
            ruleRunner('yearInput', <TranslationText tKey='sandhills.year' />, ValidationRules.requiredOr('vin', 'vin')),
            ruleRunner('makeInput', <TranslationText tKey='sandhills.make' />, ValidationRules.requiredIf(formData.yearInput != '')),
            ruleRunner('modelInput', <TranslationText tKey='sandhills.model' />, ValidationRules.requiredIf(formData.yearInput != '')),
            ruleRunner('trimInput', <TranslationText tKey='sandhills.crm.dscrm.trim' />, ValidationRules.requiredIf(formData.yearInput != '')),
            ruleRunner('vin', <TranslationText tKey='sandhills.vin' />, ValidationRules.requiredOr('year', 'year'))
        ];

        let errors = run(formData, rules);
        setValidationErrors(errors);

        if (Object.keys(errors).length <= 0) {
            if (formData.vin && formData.vin != '' && formData.year?.Text == '' && formData.make?.Text == '' && formData.model?.Text == '' && formData.trim?.Text == '') {
                getOptionsByVin(formData.vin);
            }
            else {
                getVehicleTowingCapcityResults(formData.year?.Text, formData.make?.Text, formData.model?.Text, formData.trim?.Text, false);
            }
        }
        else {
            setLoading(false); 
        }
    }

    function mapOptions(options) {
        return options?.map(item => ({ Value: item, Text: item })) ?? [];
    }

    const mapSelectedOption = (options) => {
        return options && options.length > 0 ? { Value: options[0], Text: options[0] } : { Text: '', Value: '' };
    }

    const fetchOptions = ({ url, data, key }) => {
        sandhillsRequest({ url: url, method: 'post', data: data }).then((response) => {
            if (response && response.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    [key]: response.map(item => ({ Value: item, Text: item }))
                }));
            }
        });
    }

    const handleDropdownChange = (field, newValue, resetFields = []) => {
        let url = '';
        setFormData(prev => ({
            ...prev,
            [field]: newValue,
            ...resetFields.reduce((acc, f) => ({ ...acc, [f]: { Text: '', Value: '' } }), {})
        }));
        setValidationErrors(prev => ({ ...prev, [field]: '' }));
        if (field === 'year') {
            url = routes.GetTowingCapacityCalculatorMakeOptions;
            fetchOptions({ url, data: { year: newValue?.Text ?? '' }, key: 'makeOptions' });
        } else if (field === 'make') {
            url = routes.GetTowingCapacityCalculatorModelOptions;
            fetchOptions({ url, data: { year: formData.year?.Text ?? '', make: newValue?.Text ?? '' }, key: 'modelOptions' });
        } else if (field === 'model') {
            url = routes.GetTowingCapacityCalculatorTrimOptions;
            fetchOptions({ url, data: { year: formData.year?.Text ?? '', make: formData.make?.Text ?? '', model: newValue?.Text ?? '' }, key: 'trimOptions' });
        }
    }

    const getOptionsByVin = (vin, truckCampers = false) => {
        gtmPushEvent(gtmEventType.towingGuideSearch, 'towingGuideSearchType', 'VIN');
        sandhillsRequest({
            url: routes.GetVehicleYearMakeModelTrimOptions,
            method: 'post',
            data: {
                vin: vin
            }
        }).then((response) => {
            if (response && response?.Success == true) {
                setFormData(previousState => {
                    return {
                        ...previousState,
                        yearOptions: mapOptions(response.Years),
                        makeOptions: mapOptions(response.Makes),
                        modelOptions: mapOptions(response.Models),
                        trimOptions: mapOptions(response.Trims),
                        year: mapSelectedOption(response.Years),
                        make: mapSelectedOption(response.Makes),
                        model: mapSelectedOption(response.Models),
                        trim: mapSelectedOption(response.Trims),
                    };
                })
                getVehicleTowingCapcityResults(response.Years[0], response.Makes[0], response.Models[0], response.Trims[0], truckCampers, true);
            }
            else {
                setErrorMessage(response?.Message ?? translator.translate('sandhills.towingcapacityvinerrormessage'));
                setLoading(false);
                props.setTowingResults(null, truckCampers, true);
            }
        }); 
    };

    const resetForm = () => {
        setFormData(getInitialFormData());
        setValidationErrors({});
        setErrorMessage('');
        props.setTowingResults(null, false, true);
    };

    const getVehicleTowingCapcityResults = (year, make, model, trim, truckCamper, isVinSearch = false) => {
        if (!isVinSearch) {
            gtmPushEvent(gtmEventType.towingGuideSearch, 'towingGuideSearchType', 'CMM');
        }
        if ((year && year != '0') && make && model && trim) {
            sandhillsRequest({
                url: routes.GetVehicleTowingCapacity,
                method: 'post',
                data: {
                    year: year ?? '',
                    make: make ?? '',
                    model: model ?? '',
                    trim: trim ?? '',
                    truckCamper: truckCamper,
                    clientSideReferrer: window.location.href,
                }
            }).then((response) => {
                setErrorMessage('');
                setLoading(false);
                props.setTowingResults(response, truckCamper, false);
            });
        }
        else {
            setErrorMessage(translator.translate('sandhills.towingcapacityvinerrormessage'));
            setFormData(getInitialFormData(formData?.vin));
            setLoading(false);
        }
    };

    const textChange = (e) => {
        let value = e.target.value;
        setFormData(getInitialFormData(value));
    };

    const isOptionEqualToValue = (option, value) => {
        return { Value: option?.Value, Text: option?.Text } == { Value: value?.Value, Text: value?.Text };
    };

    return (
        <>
            <Box component="form" className='towing-capacity-calculator'>
                <div className='towing-capacity-calculator-form'>
                    <Autocomplete
                        id='year'
                        name='year'
                        open={yearOpen}
                        onOpen={() => {
                            setYearOpen(true);
                        }}
                        onClose={() => {
                            setYearOpen(false);
                        }}
                        loading={yearLoading}
                        options={formData.yearOptions}
                        getOptionLabel={(option) => option?.Text}
                        autoHighlight
                        value={formData.year}
                        onChange={(event, newValue) => handleDropdownChange('year', newValue, ['make', 'model', 'trim'])}
                        inputValue={formData.yearInput}
                        onInputChange={(event, newInputValue) => {
                            setFormData(previousState => {
                                return { ...previousState, yearInput: newInputValue };
                            });
                        }}
                        isOptionEqualToValue={isOptionEqualToValue}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                error={validationErrors.yearInput != null} helperText={validationErrors.yearInput != null ? validationErrors.yearInput : ''}
                                label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.selectayear' />}
                                variant='standard'
                                InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {yearLoading ? <CircularProgress color='inherit' size={20} style={{ marginRight: '10px' }} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }} />
                        )}
                        sx={{
                            marginBottom: '8px',
                            fontSize: '14px',
                        }}
                    />
                    <Autocomplete
                        id='make'
                        name='make'
                        disabled={!formData.year?.Value}
                        open={makeOpen}
                        onOpen={() => {
                            setMakeOpen(true);
                        }}
                        onClose={() => {
                            setMakeOpen(false);
                        }}
                        loading={makeLoading}
                        options={formData.makeOptions}
                        getOptionLabel={(option) => option?.Text}
                        autoHighlight
                        value={formData.make}
                        onChange={(event, newValue) => handleDropdownChange('make', newValue, ['model', 'trim'])}
                        inputValue={formData.makeInput}
                        onInputChange={(event, newInputValue) => {
                            setFormData(previousState => {
                                return { ...previousState, makeInput: newInputValue };
                            });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} error={validationErrors.makeInput != null} helperText={validationErrors.makeInput != null ? validationErrors.makeInput : ''} disabled={!formData.year?.Value}
                                label={<TranslationText tKey='sandhills.selectamake' />}
                                variant='standard' InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {makeLoading ? <CircularProgress color='inherit' size={20} style={{ marginRight: '10px' }} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }} />
                        )}
                        isOptionEqualToValue={isOptionEqualToValue}
                        sx={{
                            marginBottom: '8px',
                            fontSize: '14px',
                        }}
                    />
                    <Autocomplete
                        id='model'
                        name='model'
                        disabled={!formData.make?.Value}
                        open={modelOpen}
                        onOpen={() => {
                            setModelOpen(true);
                        }}
                        onClose={() => {
                            setModelOpen(false);
                        }}
                        loading={modelLoading}
                        options={formData.modelOptions}
                        getOptionLabel={(option) => option?.Text}
                        autoHighlight
                        value={formData.model}
                        onChange={(event, newValue) => handleDropdownChange('model', newValue, ['trim'])}
                        inputValue={formData.modelInput}
                        onInputChange={(event, newInputValue) => {
                            setFormData(previousState => {
                                return { ...previousState, modelInput: newInputValue };
                            });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} error={validationErrors.modelInput != null} helperText={validationErrors.modelInput != null ? validationErrors.modelInput : ''} disabled={!formData.make?.Value}
                                label={<TranslationText tKey='sandhills.selectmodel' />}
                                variant='standard' InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {modelLoading ? <CircularProgress color='inherit' size={20} style={{ marginRight: '10px' }} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }} />
                        )}
                        isOptionEqualToValue={isOptionEqualToValue}
                        sx={{
                            marginBottom: '8px',
                            fontSize: '14px',
                        }}
                    />                  
                    <Autocomplete
                        id='trim'
                        name='trim'
                        disabled={!formData.model?.Value}
                        open={trimOpen}
                        onOpen={() => {
                            setTrimOpen(true);
                        }}
                        onClose={() => {
                            setTrimOpen(false);
                        }}
                        loading={trimLoading}
                        options={formData.trimOptions}
                        getOptionLabel={(option) => option?.Text}
                        autoHighlight
                        value={formData.trim}
                        onChange={(event, newValue) => handleDropdownChange('trim', newValue, [])}
                        inputValue={formData.trimInput}
                        onInputChange={(event, newInputValue) => {
                            setFormData(previousState => {
                                return { ...previousState, trimInput: newInputValue };
                            });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} error={validationErrors.trimInput != null} helperText={validationErrors.trimInput != null ? validationErrors.trimInput : ''} disabled={!formData.model?.Value}
                                label={<TranslationText tKey='sandhills.selectatrim' />}
                                variant='standard'
                                InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {trimLoading ? <CircularProgress color='inherit' size={20} style={{ marginRight: '10px' }} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }} />
                        )}
                        isOptionEqualToValue={isOptionEqualToValue}
                        sx={{
                            marginBottom: '8px',
                            fontSize: '14px',
                        }}
                    />
                    <span style={{ display: 'block', fontSize: '14px', margin: '20px 0 5px' }}>
                        <strong><TranslationText tKey='sandhills.orsearchbyvin' /></strong>
                    </span>
                    <TextField
                        id='vin'
                        name='vin'
                        value={formData.vin}
                        label="Enter Your VIN"
                        onChange={textChange}
                        variant='standard'
                        error={validationErrors.vin != null || validationErrors.yearInput != null || errorMessage != ''}
                        helperText={validationErrors.vin != null || validationErrors.yearInput != null ? validationErrors.yearInput : validationErrors.vin ? validationErrors.vin : errorMessage != '' ? errorMessage : ''}
                        sx={{
                            width: '100%',
                            fontSize: '14px',
                            marginBottom: '8px',
                            innerWidth: '100%',
                        }}
                    />
                        {loading ?
                            <div style={{textAlign: 'center'} }>
                                <CircularProgress size={80} />
                            </div> :
                            <>
                            <Button id='tg-search-btn' disabled={loading} variant="contained" className='button' onClick={handleSubmit} style={{ fontSize: '14px', padding: '6px' }}>
                                <strong><TranslationText tKey='sandhills.runsearch' /></strong>
                            </Button>
                            <Button id='tg-search-btn' variant="contained" className='button' onClick={resetForm} style={{ fontSize: '14px', padding: '6px' }}>
                                <strong><TranslationText tKey='sandhills.reset' /></strong>
                            </Button>
                            </>
                        }
                    </div> 
                <div className='towing-guide-disclaimer'>
                    <i>
                        <strong><TranslationText tKey='sandhills.important' /></strong>: <TranslationText tKey='sandhills.towingcapacitycalculatordisclaimer' />
                    </i>
                </div>
            </Box>
        </>
    )
}

export default TowingCapacityCalculator;
global.TowingCapacityCalculator = TowingCapacityCalculator;